import React, { useState, useEffect } from 'react';
import { Course, AdminCourseCreate, AdminCourseUpdate, User } from '../../types';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface CourseManagementProps {
  courses: Course[];
  onCourseCreated: (course: Course) => void;
  onCourseUpdated: (course: Course) => void;
  onCourseDeleted: (courseId: number) => void;
  onCourseSelected: (course: Course | null) => void;
  onManageLessons: (course: Course) => void;
  selectedCourse: Course | null;
}

const CourseManagement: React.FC<CourseManagementProps> = ({
  courses,
  onCourseCreated,
  onCourseUpdated,
  onCourseDeleted,
  onCourseSelected,
  onManageLessons,
  selectedCourse,
}) => {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [instructors, setInstructors] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [createFormData, setCreateFormData] = useState<AdminCourseCreate>({
    title: '',
    description: '',
    price: 0,
    instructor_email: '',
    is_published: false,
    thumbnail_url: '',
  });

  const [editFormData, setEditFormData] = useState<AdminCourseUpdate>({});

  useEffect(() => {
    fetchInstructors();
  }, []);

  useEffect(() => {
    if (selectedCourse && showEditForm) {
      setEditFormData({
        title: selectedCourse.title,
        description: selectedCourse.description,
        price: selectedCourse.price,
        instructor_email: selectedCourse.instructor?.email || '',
        is_published: selectedCourse.is_published,
        thumbnail_url: selectedCourse.thumbnail_url || '',
      });
    }
  }, [selectedCourse, showEditForm]);

  const fetchInstructors = async () => {
    try {
      const instructorsData = await adminAPI.getInstructors();
      setInstructors(instructorsData);
    } catch (error) {
      console.error('Error fetching instructors:', error);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const newCourse = await adminAPI.createCourse(createFormData);
      onCourseCreated(newCourse);
      setShowCreateForm(false);
      setCreateFormData({
        title: '',
        description: '',
        price: 0,
        instructor_email: '',
        is_published: false,
        thumbnail_url: '',
      });
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;

    setLoading(true);
    setError('');

    try {
      const updatedCourse = await adminAPI.updateCourse(selectedCourse.id, editFormData);
      onCourseUpdated(updatedCourse);
      setShowEditForm(false);
      setEditFormData({});
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteCourse(courseId);
      onCourseDeleted(courseId);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const handlePublishToggle = async (course: Course) => {
    try {
      const updatedCourse = await adminAPI.updateCourse(course.id, {
        is_published: !course.is_published,
      });
      onCourseUpdated(updatedCourse);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update course');
    }
  };

  // Check if current user can publish courses
  const canPublish = user?.is_admin || user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Course Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
        >
          Create New Course
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Course List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {courses.map((course) => (
            <li key={course.id}>
              <div className="px-4 py-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {course.title}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {course.instructor?.full_name} • ${course.price}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          course.is_published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {course.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {canPublish && (
                    <button
                      onClick={() => handlePublishToggle(course)}
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        course.is_published
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {course.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onCourseSelected(course);
                      setShowEditForm(true);
                    }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onManageLessons(course)}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded text-sm font-medium"
                  >
                    Manage Lessons
                  </button>
                  <button
                    onClick={() => handleDelete(course.id)}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Create Course Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Course</h3>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    required
                    value={createFormData.title}
                    onChange={(e) => setCreateFormData({ ...createFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    required
                    value={createFormData.description}
                    onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={createFormData.price}
                    onChange={(e) => setCreateFormData({ ...createFormData, price: parseFloat(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Instructor Email</label>
                  <select
                    required
                    value={createFormData.instructor_email}
                    onChange={(e) => setCreateFormData({ ...createFormData, instructor_email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Instructor</option>
                    {instructors.map((instructor) => (
                      <option key={instructor.id} value={instructor.email}>
                        {instructor.full_name} ({instructor.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Thumbnail URL (optional)</label>
                  <input
                    type="url"
                    value={createFormData.thumbnail_url}
                    onChange={(e) => setCreateFormData({ ...createFormData, thumbnail_url: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {canPublish && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_published"
                      checked={createFormData.is_published}
                      onChange={(e) => setCreateFormData({ ...createFormData, is_published: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_published" className="ml-2 block text-sm text-gray-900">
                      Publish immediately
                    </label>
                  </div>
                )}
                {!canPublish && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      📝 Note: Courses will be created as drafts. Only administrators can publish courses.
                    </p>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Course'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {showEditForm && selectedCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Course</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={editFormData.title || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editFormData.price || 0}
                    onChange={(e) => setEditFormData({ ...editFormData, price: parseFloat(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Instructor Email</label>
                  <select
                    value={editFormData.instructor_email || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, instructor_email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Instructor</option>
                    {instructors.map((instructor) => (
                      <option key={instructor.id} value={instructor.email}>
                        {instructor.full_name} ({instructor.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Thumbnail URL</label>
                  <input
                    type="url"
                    value={editFormData.thumbnail_url || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, thumbnail_url: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {canPublish && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_is_published"
                      checked={editFormData.is_published || false}
                      onChange={(e) => setEditFormData({ ...editFormData, is_published: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="edit_is_published" className="ml-2 block text-sm text-gray-900">
                      Published
                    </label>
                  </div>
                )}
                {!canPublish && selectedCourse && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-sm text-gray-600">
                      Publication Status: <span className={`font-medium ${selectedCourse.is_published ? 'text-green-600' : 'text-yellow-600'}`}>
                        {selectedCourse.is_published ? 'Published' : 'Draft'}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Only administrators can change publication status.
                    </p>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Course'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement; 